import styled from 'styled-components'

/* Status palette: Created = blue, In Process = orange, Completed = green, Down = red */
export const TASK_STATUS_ACCENT = {
    created: '#1565c0',
    'in-process': '#e65100',
    completed: '#2e7d32',
    approved: '#1b5e20',
    down: '#d32f2f',
    overdue: '#c62828',
}

export const TASK_STATUS_FILL = {
    created: '#e3f2fd',
    'in-process': '#fff8e1',
    completed: '#e8f5e9',
    approved: '#2e7d32',
    down: '#ffebee',
    overdue: '#ffebee',
}

const statusAccent = (variant, fallback = '#e5e7eb') =>
    TASK_STATUS_ACCENT[variant] || fallback

const statusFill = (variant, fallback = '#fff') =>
    TASK_STATUS_FILL[variant] || fallback

export const DashboardShell = styled.div`
    color: #111827;
`

export const BreadcrumbText = styled.div`
    color: #6b7280;
    font-size: 13px;
    margin-bottom: 8px;
`

export const KpiRow = styled.div`
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    margin-bottom: 8px;
    overflow-x: auto;
`

export const KpiRowMobile = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 8px;
    margin-bottom: 8px;
    overflow-x: visible;

    & > * {
        flex: none;
        width: 100%;
        min-width: 0;
    }
`

export const KpiTile = styled.div`
    flex: 1 1 0;
    min-width: 140px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 8px 10px;
    border-left: 3px solid
        ${(p) => {
            const colors = {
                blue: '#2563eb',
                amber: '#f59e0b',
                green: '#10b981',
                red: '#ef4444',
                purple: '#8b5cf6',
                slate: '#64748b',
            }
            return colors[p.$accent] || colors.blue
        }};

    @media (max-width: 930px) {
        flex: none;
        width: 100%;
        min-width: 0;
    }
`

export const KpiTileContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex-wrap: wrap;

    @media (max-width: 930px) {
        row-gap: 2px;
    }
`

export const KpiLabel = styled.div`
    font-size: 11px;
    text-transform: uppercase;
    color: #6b7280;
    letter-spacing: 0.04em;
    white-space: nowrap;
    flex-shrink: 0;

    @media (max-width: 930px) {
        flex: 1 1 auto;
        min-width: 0;
    }
`

export const KpiValue = styled.div`
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    color: #111827;
    flex-shrink: 0;
`

export const KpiDelta = styled.div`
    font-size: 11px;
    color: ${(p) => (p.$up ? '#059669' : '#6b7280')};
    white-space: nowrap;
    min-width: 0;

    @media (max-width: 930px) {
        flex: 1 1 auto;
        white-space: normal;
        overflow: visible;
        text-overflow: unset;
    }
`

export const StatusQuickFilterRow = styled.div`
    margin-bottom: 16px;
`

export const QuickFiltersBar = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 8px;
`

export const QuickFiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0;
    align-items: stretch;
`

export const QuickFiltersGridMobile = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
    align-items: stretch;
`

export const QuickFilterColumn = styled.div`
    min-width: 0;
    min-height: 100%;
    padding: 4px 14px;
    box-sizing: border-box;

    &:first-child {
        padding-left: 0;
    }

    &:last-child {
        padding-right: 0;
    }

    &:not(:last-child) {
        border-right: 1px solid #e5e7eb;
    }
`

export const QuickFilterColumnMobile = styled.div`
    min-width: 0;
    min-height: 100%;
    padding: 12px 0;
    box-sizing: border-box;

    &:first-child,
    &:last-child {
        padding-left: 0;
        padding-right: 0;
    }

    &:not(:last-child) {
        border-right: none;
        border-bottom: 1px solid #e5e7eb;
        padding-bottom: 12px;
        margin-bottom: 4px;
    }
`

export const StatusQuickFilterLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #111827;
    text-transform: none;
    letter-spacing: 0;
    margin-bottom: 6px;
`

export const StatusQuickFilterPills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`

export const StatusQuickFilterPill = styled.button`
    appearance: none;
    border: 1px solid ${(p) =>
        p.$active ? statusAccent(p.$variant, '#2563eb') : '#e5e7eb'};
    background: ${(p) =>
        p.$active ? statusFill(p.$variant, '#eff6ff') : '#f9fafb'};
    color: ${(p) => statusAccent(p.$variant, '#374151')};
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: ${(p) => (p.$active ? 700 : 500)};
    cursor: pointer;
    line-height: 1.2;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    box-shadow: ${(p) =>
        p.$active ? `0 0 0 2px ${statusFill(p.$variant, '#dbeafe')}` : 'none'};

    &:hover {
        background: ${(p) => statusFill(p.$variant, '#f3f4f6')};
        border-color: ${(p) => statusAccent(p.$variant, '#9ca3af')};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .count {
        margin-left: 4px;
        font-size: 11px;
        font-weight: 600;
        opacity: 0.85;
    }
`

export const StatusQuickFilterToggle = styled.button`
    appearance: none;
    border: 1px dashed #cbd5e1;
    background: #fff;
    color: #2563eb;
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    line-height: 1.2;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover {
        background: #eff6ff;
        border-color: #93c5fd;
        color: #1d4ed8;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`

export const InsightsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 16px;
    margin-bottom: 16px;
`

export const InsightsRowMobile = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 16px;
`

export const Panel = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px 18px;
`

export const PanelTitle = styled.h2`
    margin: 0 0 12px;
    font-size: 16px;
    font-weight: 600;
`

export const FilterBar = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 12px;
`

const FILTER_FIELD_MIN_WIDTH = '140px'

export const FilterFieldsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-end;
    width: 100%;
`

export const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1 1 0;
    min-width: ${FILTER_FIELD_MIN_WIDTH};

    label {
        font-size: 10px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    input,
    select {
        width: 100%;
        height: 38px;
        padding: 6px 10px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 13px;
        background: #fff;
        color: #111827;
        box-sizing: border-box;
    }

    input:focus,
    select:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    }

    select {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-right: 28px;
    }
`

export const FilterDateRangeGroup = styled.div`
    display: flex;
    gap: 10px;
    flex: 2 1 0;
    min-width: calc(${FILTER_FIELD_MIN_WIDTH} * 2 + 10px);
    flex-wrap: nowrap;
    align-items: flex-end;
`

export const FilterDateGroup = styled(FilterGroup)`
    flex: 1 1 0 !important;
    min-width: 0 !important;
`

export const FilterDropdownWrap = styled.div`
    width: 100%;
    min-width: 0;

    .ui.selection.dropdown.task-filter-dropdown {
        min-height: 38px !important;
        padding: 10px 2.1em 10px 10px !important;
        display: flex !important;
        align-items: center !important;
        border-radius: 6px !important;
        font-size: 13px !important;
        font-weight: 400 !important;
        border: 1px solid #d1d5db !important;
        background: #fff !important;
        box-shadow: none !important;
        width: 100% !important;
        min-width: 0 !important;
    }

    .ui.selection.dropdown.task-filter-dropdown > .text,
    .ui.selection.dropdown.task-filter-dropdown > .default.text {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        flex: 1 1 auto !important;
        min-width: 0 !important;
        max-width: 100% !important;
        color: #111827 !important;
        font-weight: 400 !important;
    }

    .ui.selection.dropdown.task-filter-dropdown > .dropdown.icon {
        color: #6b7280 !important;
        flex-shrink: 0 !important;
    }

    .ui.selection.dropdown.task-filter-dropdown.active,
    .ui.selection.dropdown.task-filter-dropdown.visible {
        z-index: 1100;
    }

    .ui.selection.dropdown.task-filter-dropdown .menu {
        max-height: 300px !important;
        overflow-y: auto !important;
        z-index: 1101 !important;
    }

    .ui.selection.dropdown.task-filter-dropdown .menu > .item {
        white-space: normal !important;
        word-break: break-word !important;
        line-height: 1.35 !important;
    }

    /* Searchable dropdown: hide text caret when closed (selection shown) */
    .ui.selection.dropdown.task-filter-dropdown:not(.active):not(.visible) > input.search {
        opacity: 0 !important;
        width: 0 !important;
        min-width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;
        caret-color: transparent !important;
        pointer-events: none !important;
    }

    .ui.selection.dropdown.task-filter-dropdown.active > input.search,
    .ui.selection.dropdown.task-filter-dropdown.visible > input.search {
        caret-color: auto !important;
        pointer-events: auto !important;
    }
`

/* Platen-style status filter dropdown (Semantic UI — native select cannot tint reliably) */
export const StatusFilterDropdownWrap = styled.div`
    width: 100%;

    .ui.selection.dropdown.task-status-filter {
        min-height: 38px !important;
        padding: 10px 2.1em 10px 10px !important;
        display: flex !important;
        align-items: center !important;
        border-radius: 6px !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        box-shadow: none !important;
    }

    .ui.selection.dropdown.task-status-filter > .text {
        font-weight: 600 !important;
    }

    .ui.selection.dropdown.task-status-filter-neutral {
        border: 1px solid #bcd4ea !important;
        background: #ffffff !important;
    }

    .ui.selection.dropdown.task-status-filter-neutral > .text {
        color: #111111 !important;
    }

    .ui.selection.dropdown.task-status-filter-neutral > .dropdown.icon {
        color: #9aa3ae !important;
    }

    .ui.selection.dropdown.task-status-filter-created {
        border: 2px solid ${TASK_STATUS_ACCENT.created} !important;
        background: ${TASK_STATUS_FILL.created} !important;
    }

    .ui.selection.dropdown.task-status-filter-created > .text,
    .ui.selection.dropdown.task-status-filter-created > .dropdown.icon {
        color: ${TASK_STATUS_ACCENT.created} !important;
    }

    .ui.selection.dropdown.task-status-filter-created.active,
    .ui.selection.dropdown.task-status-filter-created.visible,
    .ui.selection.dropdown.task-status-filter-created:focus {
        border-color: ${TASK_STATUS_ACCENT.created} !important;
        background: ${TASK_STATUS_FILL.created} !important;
    }

    .ui.selection.dropdown.task-status-filter-in-process {
        border: 2px solid ${TASK_STATUS_ACCENT['in-process']} !important;
        background: ${TASK_STATUS_FILL['in-process']} !important;
    }

    .ui.selection.dropdown.task-status-filter-in-process > .text,
    .ui.selection.dropdown.task-status-filter-in-process > .dropdown.icon {
        color: ${TASK_STATUS_ACCENT['in-process']} !important;
    }

    .ui.selection.dropdown.task-status-filter-in-process.active,
    .ui.selection.dropdown.task-status-filter-in-process.visible,
    .ui.selection.dropdown.task-status-filter-in-process:focus {
        border-color: ${TASK_STATUS_ACCENT['in-process']} !important;
        background: ${TASK_STATUS_FILL['in-process']} !important;
    }

    .ui.selection.dropdown.task-status-filter-completed {
        border: 2px solid ${TASK_STATUS_ACCENT.completed} !important;
        background: ${TASK_STATUS_FILL.completed} !important;
    }

    .ui.selection.dropdown.task-status-filter-completed > .text,
    .ui.selection.dropdown.task-status-filter-completed > .dropdown.icon {
        color: ${TASK_STATUS_ACCENT.completed} !important;
    }

    .ui.selection.dropdown.task-status-filter-completed.active,
    .ui.selection.dropdown.task-status-filter-completed.visible,
    .ui.selection.dropdown.task-status-filter-completed:focus {
        border-color: ${TASK_STATUS_ACCENT.completed} !important;
        background: ${TASK_STATUS_FILL.completed} !important;
    }

    .ui.selection.dropdown.task-status-filter-down {
        border: 2px solid ${TASK_STATUS_ACCENT.down} !important;
        background: ${TASK_STATUS_FILL.down} !important;
    }

    .ui.selection.dropdown.task-status-filter-down > .text,
    .ui.selection.dropdown.task-status-filter-down > .dropdown.icon {
        color: ${TASK_STATUS_ACCENT.down} !important;
    }

    .ui.selection.dropdown.task-status-filter-down.active,
    .ui.selection.dropdown.task-status-filter-down.visible,
    .ui.selection.dropdown.task-status-filter-down:focus {
        border-color: ${TASK_STATUS_ACCENT.down} !important;
        background: ${TASK_STATUS_FILL.down} !important;
    }

    .ui.selection.dropdown.task-status-filter-overdue {
        border: 2px solid ${TASK_STATUS_ACCENT.overdue} !important;
        background: ${TASK_STATUS_FILL.overdue} !important;
    }

    .ui.selection.dropdown.task-status-filter-overdue > .text,
    .ui.selection.dropdown.task-status-filter-overdue > .dropdown.icon {
        color: ${TASK_STATUS_ACCENT.overdue} !important;
    }

    .ui.selection.dropdown.task-status-filter-overdue.active,
    .ui.selection.dropdown.task-status-filter-overdue.visible,
    .ui.selection.dropdown.task-status-filter-overdue:focus {
        border-color: ${TASK_STATUS_ACCENT.overdue} !important;
        background: ${TASK_STATUS_FILL.overdue} !important;
    }
`

export const FilterActionsGroup = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 10px;
    margin-left: auto;
    flex-shrink: 0;
    flex-wrap: wrap;
`

export const FilterActionsGroupMobile = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 10px;
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
    margin-top: 4px;
    flex-shrink: 0;
    flex-wrap: wrap;
`

export const ViewToggle = styled.div`
    display: inline-flex;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;

    button {
        background: #fff;
        color: #6b7280;
        border: 0;
        padding: 7px 12px;
        cursor: pointer;
        font-size: 13px;
    }

    button.active {
        background: #eff6ff;
        color: #1d4ed8;
        font-weight: 600;
    }
`

export const DemoBar = styled.div`
    background: #1f2937;
    color: #f3f4f6;
    padding: 10px 16px;
    border-radius: 6px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-size: 13px;
    flex-wrap: wrap;
`

export const DemoBarLeft = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
`

export const RoleSegment = styled.div`
    background: #374151;
    border-radius: 4px;
    display: inline-flex;
    padding: 2px;

    button {
        background: transparent;
        color: #d1d5db;
        border: 0;
        padding: 4px 12px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
    }

    button.active {
        background: #2563eb;
        color: #fff;
    }
`

export const DemoHint = styled.span`
    color: #9ca3af;
    font-size: 12px;
`

export const BulkSelectionBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${(p) => {
        if (p.$between) return 'space-between'
        if (p.$alignStart) return 'flex-start'
        return 'flex-end'
    }};
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;

    .selection-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }

    .clear-btn {
        background: transparent;
        color: #2563eb;
        border: none;
        padding: 0;
        font-size: 13px;
        cursor: pointer;
        text-decoration: underline;

        &:hover {
            color: #1d4ed8;
        }
    }

    .delete-btn {
        background: #2563eb;
        color: #fff;
        border: none;
        border-radius: 6px;
        padding: 8px 14px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;

        &:hover {
            background: #1d4ed8;
        }
    }
`

export const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
    gap: 12px;
`

export const TaskCard = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 14px;
    transition: box-shadow 0.15s, border-color 0.15s, transform 0.1s;
    position: relative;
    cursor: default;

    &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        border-color: #c7d2fe;
    }

    &.qp-anchor-active {
        outline: 2px solid #2563eb;
        outline-offset: -1px;
        border-color: #2563eb !important;
    }

    &.card-selected {
        border-color: #93c5fd;
        background: #f8fafc;
    }
`

export const CardRow1 = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 6px;
`

export const CardRowLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 1;
    min-width: 0;
`

export const CardSelectWrap = styled.div`
    flex-shrink: 0;
    padding-top: 2px;
`

export const CardWho = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

export const CardAuditorName = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`

/** CrossUserAccess: non-owned task the caller can see but not edit. */
export const ViewOnlyChip = styled.span`
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.4;
    box-sizing: border-box;
    border: 1.5px solid #9ca3af;
    background: #f3f4f6;
    color: #4b5563;
    white-space: nowrap;
`

export const CardCustomerLabel = styled.div`
    font-size: 11px;
    color: #6b7280;
`

export const TemplateName = styled.span`
    font-weight: 700;
    color: #111827;
`

export const TaskTitleText = styled.span`
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #1d4ed8;
    margin-bottom: 6px;
`

export const CardTemplate = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0 2px;

    ${TemplateName} {
        flex: 1;
        min-width: 0;
        font-size: 14px;
    }
`

export const PreviewEyeButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: #f9fafb;
    color: #2563eb;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;

    &:hover {
        background: #eff6ff;
        border-color: #bfdbfe;
        color: #1d4ed8;
    }

    &:focus-visible {
        outline: 2px solid #93c5fd;
        outline-offset: 1px;
    }
`

export const TemplateCellInner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;

    .template-text {
        flex: 1;
        min-width: 0;
    }
`

export const CardTemplateCustomer = styled.div`
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 6px;
`

export const CardMeta = styled.div`
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 8px;
    flex-wrap: wrap;
    align-items: center;

    span {
        display: inline-flex;
        align-items: center;
        gap: 3px;
    }
`

export const CardProgressRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 11px;
    color: #4b5563;
`

export const CardBody = styled.div`
    flex: 1;
`

export const CardActions = styled.div`
    margin-top: 4px;
`

export const StatusPill = styled.span`
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.4;
    box-sizing: border-box;
    border: 1.5px solid ${(p) => statusAccent(p.$variant, TASK_STATUS_ACCENT.created)};
    background: ${(p) => statusFill(p.$variant, TASK_STATUS_FILL.created)};
    color: ${(p) => statusAccent(p.$variant, TASK_STATUS_ACCENT.created)};
`

/** Compact green seal-style badge: tick + APPROVED */
export const ApprovedSealBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px 2px 4px;
    border-radius: 999px;
    background: #2e7d32;
    border: 1.5px solid #1b5e20;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.3;
    box-sizing: border-box;
    white-space: nowrap;
    box-shadow: 0 1px 2px rgba(27, 94, 32, 0.25);

    .approved-seal-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #fff;
        color: #2e7d32;
        flex-shrink: 0;
    }
`

export const ScorePill = styled(StatusPill)`
    background: ${(p) => {
        const map = {
            'score-good': '#d1fae5',
            'score-ok': '#fef3c7',
            'score-warn': '#ffedd5',
            'score-bad': '#fee2e2',
        }
        return map[p.$variant] || '#d1fae5'
    }};
    color: ${(p) => {
        const map = {
            'score-good': '#065f46',
            'score-ok': '#92400e',
            'score-warn': '#9a3412',
            'score-bad': '#991b1b',
        }
        return map[p.$variant] || '#065f46'
    }};
`

export const Avatar = styled.div`
    width: ${(p) => (p.$large ? '36px' : '28px')};
    height: ${(p) => (p.$large ? '36px' : '28px')};
    border-radius: 50%;
    background: #c7d2fe;
    color: #1e3a8a;
    font-size: ${(p) => (p.$large ? '13px' : '11px')};
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`

export const ProgressBarWrap = styled.div`
    flex: 1;
    height: 6px;
    background: #f3f4f6;
    border-radius: 3px;
    overflow: hidden;
`

export const ProgressBarFill = styled.div`
    height: 100%;
    background: linear-gradient(90deg, #2563eb, #06b6d4);
    border-radius: 3px;
    width: ${(p) => p.$pct || 0}%;
`

export const TableWrap = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: auto;
`

export const TasksTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        background: #f9fafb;
        padding: 9px 12px;
        text-align: left;
        color: #374151;
        font-weight: 600;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        border-bottom: 1px solid #e5e7eb;
        white-space: nowrap;
    }

    td {
        padding: 10px 12px;
        border-bottom: 1px solid #f3f4f6;
        vertical-align: middle;
    }

    tbody tr {
        cursor: pointer;

        &:hover {
            background: #fafbfc;
        }

        &.row-active {
            background: #eff6ff;
        }
    }
`

export const MicroBar = styled.div`
    width: 80px;
    height: 5px;
    background: #f3f4f6;
    border-radius: 3px;
    overflow: hidden;
    display: inline-block;
    vertical-align: middle;
    margin-right: 6px;

    > div {
        height: 100%;
        background: linear-gradient(90deg, #2563eb, #06b6d4);
        width: ${(p) => p.$pct || 0}%;
    }
`

export const DueText = styled.span`
    font-weight: 600;
    color: ${(p) => {
        if (p.$state === 'ok') return '#065f46'
        if (p.$state === 'warn') return '#92400e'
        if (p.$state === 'bad') return '#991b1b'
        return '#374151'
    }};
`

export const PaginationFooter = styled.div`
    padding-bottom: 90px;

    @media (max-width: 767px) {
        padding-bottom: 110px;
    }
`

export const PaginationRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    font-size: 13px;
    color: #6b7280;
    flex-wrap: wrap;
    gap: 8px;

    .pagination-wrap {
        margin-left: auto;
        display: flex;
        justify-content: flex-end;
    }
`

export const LeaderboardRow = styled.div`
    display: grid;
    grid-template-columns: 24px 1fr 1fr 80px 60px;
    gap: 8px;
    align-items: center;
    padding: 4px 0;
    font-size: 12px;

    .bar-wrap {
        height: 8px;
        background: #f3f4f6;
        border-radius: 4px;
        overflow: hidden;
    }

    .bar {
        height: 100%;
        background: #2563eb;
        border-radius: 4px;
        width: ${(p) => p.$width || 0}%;
    }

    .name {
        font-weight: 500;
        color: #111827;
    }

    .small {
        color: #6b7280;
        font-size: 11px;
        text-align: right;
    }
`

export const DonutLegend = styled.div`
    font-size: 12px;
    flex: 1;
    min-width: 140px;

    .legend-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 3px 0;
    }

    .legend-label {
        display: inline-flex;
        align-items: center;
        min-width: 0;
        white-space: nowrap;
    }

    .legend-pct {
        flex-shrink: 0;
        font-weight: 500;
        color: #374151;
    }

    .sw {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 2px;
        margin-right: 6px;
        flex-shrink: 0;
    }
`

export const DonutWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`

export const DonutChartWrap = styled.div`
    position: relative;
    width: 140px;
    height: 140px;
    flex-shrink: 0;
`

export const DonutCenter = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    pointer-events: none;
`

export const EmptyState = styled.div`
    padding: 32px;
    text-align: center;
    color: #6b7280;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
`
